import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ROLES = ['manager','director','ceo','rnd_head','principal_scientist','scientist','bd','sales','mr','finance','procurement','software'];
const ROLE_LABELS = {
  manager: 'Manager', director: 'Director', ceo: 'CEO', rnd_head: 'R&D Head',
  principal_scientist: 'Principal Scientist', scientist: 'Scientist', bd: 'BD',
  sales: 'Sales', mr: 'MR', finance: 'Finance', procurement: 'Procurement', software: 'Software',
};

const MODULE_GROUPS = [
  { label: 'Core CRM', modules: ['clients','partners','customers','inquiries','activities','visits','license-expiry'] },
  { label: 'Products', modules: ['catalog','chem-structure'] },
  { label: 'Operations', modules: ['projects','orders','purchase-orders','proforma-invoice-orders','tax-invoice-orders','quotes'] },
  { label: 'Science / Quality', modules: ['samples','coa','capa','chem-structure','competitors'] },
  { label: 'Finance', modules: ['accounting','receivables','balance-sheet','gst-report','tva'] },
  { label: 'HR', modules: ['hr','attendance'] },
  { label: 'General', modules: ['dashboard','tasks','service-requests','analytics'] },
  { label: 'Admin', modules: ['users','audit-logs','company-settings','territories','data-export','email-templates','web-inquiries','webhooks','personas'] },
];

const MODULE_LABELS = {
  'clients': 'Clients', 'partners': 'Partners', 'customers': 'Customers',
  'catalog': 'Catalog', 'chem-structure': 'Chem Structure', 'inquiries': 'Inquiries',
  'projects': 'Projects', 'orders': 'Orders', 'purchase-orders': 'Purchase Orders',
  'proforma-invoice-orders': 'Proforma Invoice Orders', 'tax-invoice-orders': 'Tax Invoice Orders',
  'quotes': 'Quotes', 'visits': 'Visits', 'activities': 'Activities', 'tasks': 'Tasks',
  'service-requests': 'Service Requests', 'samples': 'Sample Mgmt', 'competitors': 'Competitors',
  'tva': 'TVA', 'license-expiry': 'License Expiry', 'coa': 'COA', 'capa': 'CAPA',
  'analytics': 'Analytics', 'accounting': 'Accounting', 'receivables': 'Receivables',
  'balance-sheet': 'Balance Sheet', 'gst-report': 'GST Report', 'hr': 'HR',
  'attendance': 'Attendance', 'territories': 'Territories', 'data-export': 'Data Export',
  'email-templates': 'Email Templates', 'web-inquiries': 'Web Inquiries',
  'audit-logs': 'Audit Logs', 'users': 'Users', 'company-settings': 'Company Settings',
  'webhooks': 'Webhooks', 'personas': 'Personas', 'dashboard': 'Dashboard',
};

const OPS = ['can_list','can_read','can_create','can_update','can_delete'];
const OP_LABELS = { can_list: 'List', can_read: 'Read', can_create: 'Create', can_update: 'Update', can_delete: 'Delete' };
const OP_COLORS = { can_list: '#2563eb', can_read: '#059669', can_create: '#7c3aed', can_update: '#d97706', can_delete: '#dc2626' };

const emptyPerm = () => ({ can_list: false, can_read: false, can_create: false, can_update: false, can_delete: false });

export default function RolePermissions() {
  const [activeRole, setActiveRole] = useState('manager');
  const [allPerms, setAllPerms] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/role-permissions');
      setAllPerms(data.data?.matrix || {});
      setDirty({});
    } catch {
      showToast('Failed to load permissions', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getRolePerm = (role, module) =>
    dirty[role]?.[module] ?? allPerms[role]?.[module] ?? emptyPerm();

  const toggle = (module, op) => {
    const cur = getRolePerm(activeRole, module);
    const next = { ...cur, [op]: !cur[op] };
    setDirty(prev => ({
      ...prev,
      [activeRole]: { ...(prev[activeRole] || allPerms[activeRole] || {}), [module]: next },
    }));
  };

  const toggleAll = (module, val) => {
    const next = { can_list: val, can_read: val, can_create: val, can_update: val, can_delete: val };
    setDirty(prev => ({
      ...prev,
      [activeRole]: { ...(prev[activeRole] || allPerms[activeRole] || {}), [module]: next },
    }));
  };

  const toggleOp = (op, val) => {
    const roleData = { ...(dirty[activeRole] || allPerms[activeRole] || {}) };
    getAllModules().forEach(mod => {
      roleData[mod] = { ...getRolePerm(activeRole, mod), [op]: val };
    });
    setDirty(prev => ({ ...prev, [activeRole]: roleData }));
  };

  const getAllModules = () => {
    const seen = new Set();
    return MODULE_GROUPS.flatMap(g => g.modules).filter(m => { if (seen.has(m)) return false; seen.add(m); return true; });
  };

  const save = async () => {
    if (!dirty[activeRole]) return showToast('No changes to save');
    setSaving(true);
    try {
      await api.put(`/role-permissions/${activeRole}`, { permissions: dirty[activeRole] });
      setAllPerms(prev => ({ ...prev, [activeRole]: { ...(prev[activeRole] || {}), ...dirty[activeRole] } }));
      setDirty(prev => { const d = { ...prev }; delete d[activeRole]; return d; });
      showToast(`${ROLE_LABELS[activeRole]} permissions saved`);
    } catch (e) {
      showToast(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset ALL roles to factory defaults? This cannot be undone.')) return;
    setResetting(true);
    try {
      await api.post('/role-permissions/reset');
      await load();
      showToast('All permissions reset to defaults');
    } catch (e) {
      showToast(e.response?.data?.message || 'Reset failed', 'error');
    } finally {
      setResetting(false);
    }
  };

  const hasDirty = !!dirty[activeRole];

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <div style={{ padding: '24px 20px', maxWidth: 1200, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#dc2626' : '#059669',
          color: '#fff', padding: '10px 18px', borderRadius: 8,
          fontWeight: 500, fontSize: '0.875rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Role Permissions</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '4px 0 0' }}>
            Configure what each role can do across all modules. Changes take effect immediately after saving.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {hasDirty && (
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
              {saving ? 'Saving…' : `Save ${ROLE_LABELS[activeRole]}`}
            </button>
          )}
          <button onClick={reset} disabled={resetting}
            style={{ padding: '8px 16px', background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: '0.875rem' }}>
            {resetting ? 'Resetting…' : 'Reset All to Defaults'}
          </button>
        </div>
      </div>

      {/* Role Tabs */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {ROLES.map(r => (
          <button key={r} onClick={() => setActiveRole(r)}
            style={{
              padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontWeight: activeRole === r ? 700 : 500, fontSize: '0.875rem',
              color: activeRole === r ? '#2563eb' : 'var(--text-muted)',
              borderBottom: activeRole === r ? '2px solid #2563eb' : '2px solid transparent',
              borderRadius: '4px 4px 0 0', transition: 'all 0.15s',
              position: 'relative',
            }}>
            {ROLE_LABELS[r]}
            {dirty[r] && <span style={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, background: '#d97706', borderRadius: '50%' }} />}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        {OPS.map(op => (
          <div key={op} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: OP_COLORS[op], display: 'inline-block' }} />
            {OP_LABELS[op]}
          </div>
        ))}
      </div>

      {/* Column header with bulk toggles */}
      <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '8px 8px 0 0', padding: '8px 12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '220px 40px repeat(5, 1fr)', gap: 4, alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Module</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center' }}>All</div>
          {OPS.map(op => (
            <div key={op} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: OP_COLORS[op] }}>{OP_LABELS[op]}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button title={`Grant ${OP_LABELS[op]} to all`}
                  onClick={() => toggleOp(op, true)}
                  style={{ padding: '1px 6px', fontSize: '0.65rem', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#fff', color: '#555' }}>
                  all
                </button>
                <button title={`Revoke ${OP_LABELS[op]} from all`}
                  onClick={() => toggleOp(op, false)}
                  style={{ padding: '1px 6px', fontSize: '0.65rem', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#fff', color: '#555' }}>
                  none
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Module rows by group */}
      <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        {MODULE_GROUPS.map((grp, gi) => {
          const uniqueMods = [...new Set(grp.modules)];
          return (
            <React.Fragment key={grp.label}>
              <div style={{ background: '#f1f5f9', padding: '5px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>
                {grp.label}
              </div>
              {uniqueMods.map((mod, mi) => {
                const perm = getRolePerm(activeRole, mod);
                const allOn = OPS.every(op => perm[op]);
                const anyOn = OPS.some(op => perm[op]);
                const isDirtyMod = !!dirty[activeRole]?.[mod];
                return (
                  <div key={mod} style={{
                    display: 'grid', gridTemplateColumns: '220px 40px repeat(5, 1fr)', gap: 4,
                    alignItems: 'center', padding: '7px 12px',
                    background: isDirtyMod ? '#fffbeb' : (mi % 2 === 0 ? '#fff' : '#fafafa'),
                    borderBottom: '1px solid #f1f5f9',
                  }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                      {MODULE_LABELS[mod] || mod}
                    </div>
                    {/* All toggle */}
                    <div style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={allOn} ref={el => { if (el) el.indeterminate = !allOn && anyOn; }}
                        onChange={e => toggleAll(mod, e.target.checked)}
                        style={{ cursor: 'pointer', width: 15, height: 15 }} />
                    </div>
                    {OPS.map(op => (
                      <div key={op} style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={!!perm[op]}
                          onChange={() => toggle(mod, op)}
                          style={{ cursor: 'pointer', accentColor: OP_COLORS[op], width: 15, height: 15 }} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {/* Bottom save bar */}
      {hasDirty && (
        <div style={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={save} disabled={saving}
            style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(37,99,235,0.4)' }}>
            {saving ? 'Saving…' : `Save ${ROLE_LABELS[activeRole]} Permissions`}
          </button>
        </div>
      )}
    </div>
  );
}
